import { useEffect, useRef, useState } from "react";
import { Crosshair } from "lucide-react";

/**
 * Touch-only virtual joystick + shoot button overlay for /pilot.
 *
 * Design decisions:
 * - Left thumb → 128px joystick with a 44px draggable knob. Clamped to a 48px radius,
 *   so the ratio (x/48, y/48) gives -1..1 that maps 1:1 to gameRef.current.state.touchInput.
 * - Right thumb → circular FIRE button. Presses down = state.shooting = true.
 *
 * Renders only when the device likely supports touch (touchstart-supported OR max-touch-points > 0).
 * Skips on desktop entirely — keyboard/mouse still work as before.
 *
 * Never blocks non-touch pointer events (`touch-none` + `pointer-events: none` on wrapper — only
 * the specific handles are interactive).
 */
export default function MobileControls({ gameRef, active }) {
  const [isTouch, setIsTouch] = useState(false);
  const joyRef = useRef(null);
  const knobRef = useRef(null);
  const activeJoyPointer = useRef(null);
  const activeShootPointer = useRef(null);

  useEffect(() => {
    const forced = (typeof window !== "undefined") && new URLSearchParams(window.location.search).get("touch") === "1";
    const touch = forced || ((typeof window !== "undefined") && (
      "ontouchstart" in window || (navigator.maxTouchPoints || 0) > 0
    ));
    setIsTouch(touch);
  }, []);

  useEffect(() => {
    if (!isTouch) return;
    const joy = joyRef.current;
    const knob = knobRef.current;
    if (!joy || !knob) return;

    const MAX_RADIUS = 48;

    const setKnob = (dx, dy) => {
      const dist = Math.hypot(dx, dy);
      const clamped = Math.min(dist, MAX_RADIUS);
      const angle = Math.atan2(dy, dx);
      const kx = Math.cos(angle) * clamped;
      const ky = Math.sin(angle) * clamped;
      knob.style.transform = `translate(${kx}px, ${ky}px)`;
      const state = gameRef.current?.state;
      if (state) {
        state.touchInput.x = kx / MAX_RADIUS;
        state.touchInput.y = ky / MAX_RADIUS;
      }
    };

    const onJoyPointerDown = (e) => {
      if (activeJoyPointer.current !== null) return;
      activeJoyPointer.current = e.pointerId;
      joy.setPointerCapture(e.pointerId);
      const rect = joy.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      setKnob(e.clientX - cx, e.clientY - cy);
    };
    const onJoyPointerMove = (e) => {
      if (e.pointerId !== activeJoyPointer.current) return;
      const rect = joy.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      setKnob(e.clientX - cx, e.clientY - cy);
    };
    const onJoyPointerUp = (e) => {
      if (e.pointerId !== activeJoyPointer.current) return;
      activeJoyPointer.current = null;
      try { joy.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
      knob.style.transform = "translate(0px, 0px)";
      const state = gameRef.current?.state;
      if (state) { state.touchInput.x = 0; state.touchInput.y = 0; }
    };

    joy.addEventListener("pointerdown", onJoyPointerDown);
    joy.addEventListener("pointermove", onJoyPointerMove);
    joy.addEventListener("pointerup", onJoyPointerUp);
    joy.addEventListener("pointercancel", onJoyPointerUp);
    return () => {
      joy.removeEventListener("pointerdown", onJoyPointerDown);
      joy.removeEventListener("pointermove", onJoyPointerMove);
      joy.removeEventListener("pointerup", onJoyPointerUp);
      joy.removeEventListener("pointercancel", onJoyPointerUp);
    };
  }, [isTouch, gameRef]);

  const onShootDown = (e) => {
    if (activeShootPointer.current !== null) return;
    activeShootPointer.current = e.pointerId;
    e.currentTarget.setPointerCapture(e.pointerId);
    const state = gameRef.current?.state;
    if (state) state.shooting = true;
  };
  const onShootUp = (e) => {
    if (e.pointerId !== activeShootPointer.current) return;
    activeShootPointer.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    const state = gameRef.current?.state;
    if (state) state.shooting = false;
  };

  if (!isTouch || !active) return null;

  return (
    <div className="fixed inset-0 z-40 pointer-events-none select-none touch-none" data-testid="mobile-controls">
      {/* Joystick — left bottom */}
      <div className="absolute left-4 bottom-6 pointer-events-auto touch-none">
        <div
          ref={joyRef}
          data-testid="mobile-joystick"
          className="relative w-32 h-32 rounded-full border-2 border-primary/50 bg-primary/10 backdrop-blur-sm touch-none flex items-center justify-center"
        >
          <div
            ref={knobRef}
            data-testid="mobile-joystick-knob"
            className="w-11 h-11 rounded-full bg-primary/80 shadow-[0_0_20px_rgba(0,255,255,0.5)] transition-transform-none pointer-events-none"
          />
        </div>
        <p className="text-center mt-1 font-mono text-[8px] tracking-widest text-primary/70">MOVER</p>
      </div>

      {/* Shoot button — right bottom */}
      <div className="absolute right-4 bottom-8 pointer-events-auto touch-none">
        <button
          type="button"
          onPointerDown={onShootDown}
          onPointerUp={onShootUp}
          onPointerCancel={onShootUp}
          onPointerLeave={onShootUp}
          data-testid="mobile-shoot"
          aria-label="Atirar"
          className="w-20 h-20 rounded-full bg-secondary/90 border-4 border-secondary shadow-[0_0_25px_rgba(255,0,150,0.6)] active:scale-90 transition-transform grid place-items-center touch-none"
        >
          <Crosshair className="h-8 w-8 text-black" strokeWidth={3} />
        </button>
        <p className="text-center mt-1 font-mono text-[8px] tracking-widest text-secondary/80">ATIRAR</p>
      </div>
    </div>
  );
}
